import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CreditCard, Rocket, Store, Mail, Image as ImageIcon, Package, Tag, Palette,
  Layers, Users, MessageSquare, Plug, Globe, ShoppingCart, LifeBuoy,
  CheckCircle2, AlertTriangle, ArrowRight,
} from "lucide-react";

const COMPANY = "Hephy 3D";
const SUPPORT_EMAIL = "hephesteus3d@gmail.com";

const STEPS = [
  {
    id: "stripe",
    icon: CreditCard,
    title: "Conecta Stripe para cobrar (¡lo primero!)",
    time: "5 min",
    important: true,
    body: (
      <>
        <p><b>Sin esto no puedes recibir dinero.</b> Stripe es el servicio que cobra con tarjeta a tus clientes y te deposita el dinero a tu banco. Conéctalo antes que nada:</p>
        <ol>
          <li>En el menú de la izquierda entra a <span className="kbd">Ajustes</span>.</li>
          <li>Busca la sección <b>“Cobros / Stripe”</b> y pulsa el botón <b>“Conectar Stripe”</b>.</li>
          <li>Se abrirá la página de Stripe. Rellena tus datos: nombre, tu negocio, y tu <b>cuenta de banco</b> donde quieres recibir el dinero.</li>
          <li>Cuando termines, Stripe te devuelve a tu panel y verás <b>“Cobros activados”</b> en verde.</li>
        </ol>
        <p className="mt-2">Hasta que Stripe diga “activado”, tu tienda <b>no podrá cobrar</b> pedidos en línea.</p>
      </>
    ),
  },
  {
    id: "cuenta",
    icon: Rocket,
    title: "Entra a tu panel y cambia tu contraseña",
    time: "1 min",
    body: (
      <>
        <ol>
          <li>Abre el correo de bienvenida que te enviamos: trae tu usuario y una contraseña temporal.</li>
          <li>Entra en <span className="kbd">Iniciar sesión</span> con esos datos.</li>
          <li>Ve a <span className="kbd">Ajustes</span> y cambia la contraseña por una tuya.</li>
        </ol>
        <p className="mt-2">Desde el panel controlas todo: cobros, tienda, productos, pedidos, equipo y diseño.</p>
      </>
    ),
  },
  {
    id: "tienda",
    icon: Store,
    title: "Configura tu tienda",
    time: "5 min",
    body: (
      <>
        <p>Entra a <span className="kbd">Tienda</span> y completa los datos de tu negocio:</p>
        <ul>
          <li><b>Nombre del negocio</b> y <b>enlace (slug)</b> — así se verá tu dirección: <code>/tienda/mi-joyeria</code>.</li>
          <li><b>Logo</b> y colores de tu marca.</li>
          <li><b>Correo de pedidos</b>: la dirección donde quieres <b>recibir cada pedido nuevo</b>. ¡Importantísimo!</li>
          <li><b>Moneda</b>, <b>impuesto</b> y <b>costo de envío</b> por defecto.</li>
        </ul>
        <p className="mt-2">Pulsa <b>Guardar</b> al terminar.</p>
      </>
    ),
  },
  {
    id: "gmail",
    icon: Mail,
    title: "Conecta tu Gmail para enviar correos",
    time: "2 min",
    important: true,
    body: (
      <>
        <p>Sirve para que te lleguen los correos de los pedidos y para que tus clientes reciban su confirmación.</p>
        <ol>
          <li>Ve a <span className="kbd">Ajustes → Mi correo de envío</span>.</li>
          <li>Pulsa <b>“Conectar con Google”</b>, inicia sesión y acepta el permiso de envío.</li>
          <li>Verás <b>“Gmail conectado”</b>. Pulsa <b>“Enviar correo de prueba”</b> para confirmar que funciona.</li>
        </ol>
        <p className="mt-2">Solo se hace <b>una vez</b>. No necesitas contraseñas de aplicación: usa el botón de Google.</p>
      </>
    ),
  },
  {
    id: "logos",
    icon: ImageIcon,
    title: "Sube tus logos y crea colecciones",
    time: "5 min",
    body: (
      <>
        <p>Si vendes productos personalizables, en <span className="kbd">Logos</span> sube las imágenes que tus clientes podrán poner en sus diseños.</p>
        <ul>
          <li>Usa imágenes <b>PNG con fondo transparente</b> para mejor resultado.</li>
          <li>Agrupa tus productos en <b>colecciones</b> (ej. “Anillos”, “Cadenas”) para que sea fácil navegar.</li>
        </ul>
      </>
    ),
  },
  {
    id: "productos",
    icon: Package,
    title: "Añade tus productos",
    time: "10 min",
    body: (
      <>
        <p>En <span className="kbd">Productos</span> pulsa <b>“Añadir producto”</b> y crea tu catálogo:</p>
        <ul>
          <li><b>Producto normal:</b> foto, título, descripción y precio. Tendrá el botón <b>“Agregar al carrito”</b>.</li>
          <li><b>Producto personalizable / 3D:</b> súbele su modelo 3D y las opciones. Tendrá el botón <b>“Personalizar”</b>.</li>
          <li>Pon al menos <b>una foto buena</b> por producto; es lo que más vende.</li>
        </ul>
      </>
    ),
  },
  {
    id: "pagina-producto",
    icon: Palette,
    title: "Ajusta la página de producto",
    time: "5 min",
    body: (
      <>
        <p>En <span className="kbd">Diseño → Plantilla de página de producto</span> decides cómo se ve <b>cada</b> producto:</p>
        <ul>
          <li>Posición y tamaño de las imágenes, el título, la descripción y los botones.</li>
          <li>El campo donde el cliente deja una <b>petición especial</b> que viaja con el pedido.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ofertas",
    icon: Tag,
    title: "Crea promociones (opcional)",
    time: "3 min",
    body: (
      <>
        <p>En <span className="kbd">Promociones</span> lanzas ofertas:</p>
        <ul>
          <li>Crea una promoción, elige los productos y pon el <b>porcentaje de descuento</b>.</li>
          <li>Actívala o desactívala cuando quieras.</li>
          <li>Tus clientes verán el <b>precio tachado</b>, el precio de oferta y una insignia.</li>
        </ul>
      </>
    ),
  },
  {
    id: "diseno",
    icon: Palette,
    title: "Diseña tu sitio",
    time: "10 min",
    body: (
      <>
        <p>En <span className="kbd">Diseño</span> armas tu tienda arrastrando bloques:</p>
        <ul>
          <li>Encabezado con menús y enlaces, y la <b>zona inferior</b> con columnas configurables.</li>
          <li>Portada, galerías, banners, blog y pie de página.</li>
          <li>Pulsa <b>Vista previa</b> para ver cómo queda antes de publicar.</li>
        </ul>
      </>
    ),
  },
  {
    id: "fases",
    icon: Layers,
    title: "Crea tus fases de producción",
    time: "3 min",
    body: (
      <>
        <p>En <span className="kbd">Pedidos → Fases de producción</span> defines por qué etapas pasa cada pieza (ej. “Cera”, “Fundición”, “Pulido”, “Entregado”).</p>
        <ul>
          <li>Crea, renombra, reordena o borra fases.</li>
          <li>Estas fases <b>se ven automáticamente en el portal de tu equipo</b>, para que tus vendedores informen al cliente el estado de su pedido.</li>
        </ul>
      </>
    ),
  },
  {
    id: "equipo",
    icon: Users,
    title: "Invita a tu equipo (opcional)",
    time: "5 min",
    body: (
      <>
        <p>En <span className="kbd">Equipo</span> das acceso a ayudantes y vendedores sin darles control total:</p>
        <ul>
          <li>Crea cada miembro con su correo y contraseña.</li>
          <li>Marca sus <b>permisos</b>: tomar pedidos, ver fases, gestionar productos/logos, etc.</li>
          <li>Tus vendedores entran por <code>/equipo</code> con su propio acceso.</li>
          <li>Cada vendedor puede conectar su Gmail; si no lo hace, los correos salen desde el tuyo.</li>
        </ul>
      </>
    ),
  },
  {
    id: "plantillas",
    icon: MessageSquare,
    title: "Personaliza los mensajes al cliente",
    time: "4 min",
    body: (
      <>
        <p>En <span className="kbd">Equipo → Plantillas</span> escribes los textos que recibe el cliente por <b>correo y WhatsApp</b>:</p>
        <ul>
          <li>Mensaje al confirmar el pedido, enlace de pago y recordatorios de saldo.</li>
          <li>Puedes usar etiquetas que se rellenan solas: <code>{"{nombre_cliente}"}</code>, <code>{"{numero_pedido}"}</code>, <code>{"{balance}"}</code>, <code>{"{nombre_tienda}"}</code>.</li>
        </ul>
        <p className="mt-2">Estos mensajes se usan tanto en tu panel como en el portal de tu equipo.</p>
      </>
    ),
  },
  {
    id: "wix",
    icon: Plug,
    title: "Conecta Wix (opcional)",
    time: "5 min",
    body: (
      <>
        <p>Si ya tienes una tienda en <b>Wix</b>, puedes conectarla para sincronizar tus productos:</p>
        <ol>
          <li>Entra a <span className="kbd">Integraciones</span>.</li>
          <li>En la tarjeta de <b>Wix</b>, sigue los pasos para conectar tu sitio (desde el App Market de Wix o con tu clave de API).</li>
          <li>Cuando diga <b>“Conectado”</b>, tus productos se podrán enviar a Wix.</li>
        </ol>
      </>
    ),
  },
  {
    id: "shopify",
    icon: Plug,
    title: "Conecta Shopify (opcional)",
    time: "5 min",
    body: (
      <>
        <p>Si vendes en <b>Shopify</b>, también puedes conectarla:</p>
        <ol>
          <li>Entra a <span className="kbd">Integraciones</span>.</li>
          <li>En la tarjeta de <b>Shopify</b>, escribe el dominio de tu tienda (ej. <code>mi-tienda.myshopify.com</code>) y pulsa <b>Conectar</b>.</li>
          <li>Autoriza la app en Shopify y volverás a tu panel con la conexión lista.</li>
        </ol>
      </>
    ),
  },
  {
    id: "publicar",
    icon: Globe,
    title: "Publica y comparte tu tienda",
    time: "1 min",
    body: (
      <>
        <p>Cuando todo esté listo, tu tienda vive en un enlace público: <code>/tienda/tu-nombre</code>.</p>
        <ul>
          <li>Compártelo en redes sociales, WhatsApp y con tus clientes.</li>
          <li>Más adelante puedes conectar tu propio dominio.</li>
        </ul>
      </>
    ),
  },
  {
    id: "prueba",
    icon: ShoppingCart,
    title: "Haz un pedido de prueba",
    time: "3 min",
    body: (
      <>
        <p>Antes de lanzar, comprueba que todo funciona de principio a fin:</p>
        <ol>
          <li>Entra a tu tienda, elige un producto y completa un pedido de prueba.</li>
          <li>Confirma que te llega el <b>correo del pedido</b> a tu correo de pedidos.</li>
          <li>Revisa que la imagen del diseño y los logos elegidos aparezcan en el correo y en Pedidos.</li>
        </ol>
      </>
    ),
  },
];

function StepCard({ step, index }) {
  const Icon = step.icon;
  return (
    <motion.section
      id={step.id}
      data-testid={`guide-step-${step.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.03, 0.15) }}
      className={`scroll-mt-24 rounded-2xl border p-6 sm:p-8 ${
        step.important ? "border-gold/40 bg-gold/[0.04]" : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-start gap-4">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
          step.important ? "bg-gold text-obsidian" : "bg-white/5 text-gold"
        }`}>
          <Icon className="h-6 w-6" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">Paso {index + 1}</span>
            {step.time && (
              <span className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-neutral-400">⏱ {step.time}</span>
            )}
            {step.important && (
              <span className="rounded-full bg-gold px-2 py-0.5 text-[11px] font-semibold text-obsidian">Imprescindible</span>
            )}
          </div>
          <h2 className="mt-1 font-serif text-2xl text-white">{step.title}</h2>
          <div className="guide-body mt-3 space-y-2 text-[15px] leading-relaxed text-neutral-300">
            {step.body}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

export default function OnboardingGuide() {
  return (
    <div className="min-h-screen bg-obsidian text-neutral-200" data-testid="onboarding-guide-page">
      <style>{`
        .guide-body ul { list-style: disc; padding-left: 1.25rem; margin-top: .25rem; }
        .guide-body ol { list-style: decimal; padding-left: 1.25rem; margin-top: .25rem; }
        .guide-body li { margin: .25rem 0; }
        .guide-body code { background: rgba(255,255,255,.08); padding: 1px 6px; border-radius: 6px; font-size: 13px; color:#E8C766; }
        .kbd { background: rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.14); padding: 1px 7px; border-radius: 6px; font-size: 13px; color:#fff; white-space:nowrap; }
      `}</style>

      <header className="sticky top-0 z-20 border-b border-white/10 bg-obsidian/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="guide-home-link">
            <img src="/hephy-logo.png" alt={COMPANY} className="h-8 w-8" />
            <span className="font-serif text-lg">{COMPANY}</span>
          </Link>
          <Link to="/login" className="text-sm text-neutral-400 transition-colors hover:text-gold" data-testid="guide-login-link">
            Entrar a mi panel →
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Guía de inicio</p>
          <h1 className="mt-3 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl">
            Pon tu tienda en marcha,<br className="hidden sm:block" /> paso a paso
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-neutral-400">
            Bienvenido a {COMPANY}. Esta guía te lleva de la mano, como si nunca hubieras hecho una tienda. Sigue los pasos en orden y en una tarde estarás vendiendo.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-8 flex items-start gap-3 rounded-2xl border border-gold/40 bg-gold/[0.06] p-5"
          data-testid="guide-important-callout"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
          <div className="text-sm text-neutral-200">
            <b className="text-white">Los 2 pasos que no puedes saltarte:</b> 1) <b>Conectar Stripe</b> (Paso 1) para poder cobrar, y 2) <b>Conectar tu Gmail</b> (Paso 4) para recibir los correos de tus pedidos. Ambos se hacen una sola vez.
          </div>
        </motion.div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[230px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">En esta guía</p>
              <nav className="space-y-1">
                {STEPS.map((s, i) => (
                  <a
                    key={s.id} href={`#${s.id}`} data-testid={`guide-toc-${s.id}`}
                    className="group flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-neutral-400 transition-colors hover:bg-white/5 hover:text-gold"
                  >
                    <span className="font-mono text-xs text-neutral-600 group-hover:text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <span className="truncate">{s.title}</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <div className="space-y-5">
            {STEPS.map((s, i) => (
              <StepCard key={s.id} step={s} index={i} />
            ))}

            <motion.section
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8"
              data-testid="guide-done"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                  <CheckCircle2 className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <div>
                  <h2 className="font-serif text-2xl text-white">¡Listo! Ya puedes vender</h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-neutral-300">
                    Con esto tu tienda está operativa. ¿Dudas en algún paso? Escríbenos y te ayudamos con tu onboarding.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <Link
                      to="/login" data-testid="guide-cta-dashboard"
                      className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-obsidian transition-transform hover:-translate-y-0.5"
                    >
                      Ir a mi panel <ArrowRight className="h-4 w-4" />
                    </Link>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`} data-testid="guide-cta-support"
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white transition-colors hover:border-gold hover:text-gold"
                    >
                      <LifeBuoy className="h-4 w-4" /> Contactar soporte
                    </a>
                  </div>
                </div>
              </div>
            </motion.section>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-5xl px-6 py-8 text-xs text-neutral-600">
          © {new Date().getFullYear()} {COMPANY}. Guía de inicio para nuevos clientes · <a href={`mailto:${SUPPORT_EMAIL}`} className="text-neutral-500 hover:text-gold">{SUPPORT_EMAIL}</a>
        </div>
      </footer>
    </div>
  );
}
